import React from 'react';
import Button from '../../../components/Button';
import { useCardTemplatesLogic } from './CardTemplatesLogic';

/**
 * Component for managing card templates in system settings
 */
const CardTemplatesSection = () => {
  const {
    cardTemplates,
    loading,
    error,
    saveSuccess,
    selectedTemplate,
    editMode,
    templateFormData,
    selectedSubstep,
    substepFormData,
    optionInput,
    handleSelectTemplate,
    handleCreateNewTemplate,
    handleEditTemplate,
    handleCancelEditTemplate,
    handleSaveTemplate,
    handleDeleteTemplate,
    handleTemplateFormChange,
    handleCreateNewSubstep,
    handleSelectSubstep,
    handleSubstepFormChange,
    handleAddOption,
    handleRemoveOption,
    handleSaveSubstep,
    handleDeleteSubstep,
    handleInitializeFromJson,
    setOptionInput
  } = useCardTemplatesLogic();

  return (
    <div className="bg-gray-900 rounded-xl shadow-xl p-6 mb-8 border border-gray-800">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-blue-400">Card Templates Management</h2>
        <div className="flex gap-2">
          <Button 
            onClick={handleInitializeFromJson}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Initialize Templates from JSON
          </Button>
          <Button 
            onClick={handleCreateNewTemplate}
            className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
          >
            Create New Template
          </Button>
        </div>
      </div>
      
      <p className="text-gray-400 mb-6 text-right">Manage card templates for application deployment processes</p>
      
      {/* Error message */}
      {error && (
        <div className="bg-red-900/30 border-r-4 border-red-500 text-red-300 p-4 mb-6 rounded-l text-right" role="alert">
          <p>{error}</p>
        </div>
      )}
      
      {/* Success message */}
      {saveSuccess && (
        <div className="bg-green-900/30 border-r-4 border-green-500 text-green-300 p-4 mb-6 rounded-l text-right" role="alert">
          <p>Changes saved successfully!</p>
        </div>
      )}
      
      {/* Loading state */}
      {loading && (
        <div className="flex justify-center my-8">
          <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      )}
      
      {!loading && (
        <div className="grid grid-cols-12 gap-6">
          {/* Template List */}
          <div className="col-span-5 bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
            <h2 className="text-xl font-bold mb-4 text-primary text-right">Templates</h2>
            <div className="space-y-2 max-h-[calc(100vh-250px)] overflow-y-auto">
              {cardTemplates.map(template => (
                <div 
                  key={template.id}
                  onClick={() => handleSelectTemplate(template)}
                  className={`p-3 rounded-lg cursor-pointer transition-all duration-300 ${
                    selectedTemplate?.id === template.id
                      ? 'bg-primary/20 border border-primary/40'
                      : 'bg-gray-700/50 border border-gray-600/30 hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-600/60 text-center">
                      {template.type}
                    </span>
                    <div className="font-medium text-right">{template.title}</div>
                  </div>
                  <div className="text-xs text-gray-400 mt-1 truncate text-right">
                    {template.description?.substring(0, 60)}...
                  </div>
                </div>
              ))}
              
              {cardTemplates.length === 0 && (
                <div className="text-center text-gray-400 py-8">
                  No templates. Click "Create New Template" to get started.
                </div>
              )}
            </div>
          </div>

          {/* Template Editor */}
          <div className="col-span-7">
            {(selectedTemplate || editMode) ? (
              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex gap-2">
                    {!editMode ? (
                      <>
                        <Button 
                          onClick={handleEditTemplate}
                          className="px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-light text-sm"
                        >
                          Edit
                        </Button>
                        <Button 
                          onClick={handleDeleteTemplate}
                          className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                        >
                          Delete
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button 
                          onClick={handleSaveTemplate}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                        >
                          Save
                        </Button>
                        <Button 
                          onClick={handleCancelEditTemplate}
                          className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                  <h2 className="text-xl font-bold text-primary text-right">
                    {editMode 
                      ? selectedTemplate ? 'Edit Template' : 'Create New Template'
                      : 'Template Details'
                    }
                  </h2>
                </div>

                {editMode ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-3 text-right">
                        <label className="block text-sm text-gray-400">Template Type</label>
                        <select
                          name="type"
                          value={templateFormData.type}
                          onChange={handleTemplateFormChange}
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2.5 text-white focus:ring-primary focus:border-primary text-right"
                        >
                          <option value="welcome">Welcome Card</option>
                          <option value="stage">Stage Card</option>
                          <option value="information">Information Card</option>
                        </select>
                      </div>
                      <div className="space-y-3 text-right">
                        <label className="block text-sm text-gray-400">Display Order</label>
                        <input
                          type="number"
                          name="order"
                          value={templateFormData.order}
                          onChange={handleTemplateFormChange}
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2.5 text-white focus:ring-primary focus:border-primary text-right"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-3 text-right">
                      <label className="block text-sm text-gray-400">Title</label>
                      <input
                        type="text"
                        name="title"
                        value={templateFormData.title}
                        onChange={handleTemplateFormChange}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2.5 text-white focus:ring-primary focus:border-primary text-right"
                        placeholder="Enter title..."
                        dir="rtl"
                      />
                    </div>
                    
                    <div className="space-y-3 text-right">
                      <label className="block text-sm text-gray-400">Description</label>
                      <textarea
                        name="description"
                        value={templateFormData.description}
                        onChange={handleTemplateFormChange}
                        rows={3}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2.5 text-white focus:ring-primary focus:border-primary text-right"
                        placeholder="Enter description..."
                        dir="rtl"
                      />
                    </div>
                    
                    <div className="space-y-3 text-right">
                      <label className="block text-sm text-gray-400">Additional Information (Optional)</label>
                      <textarea
                        name="additionalInfo"
                        value={templateFormData.additionalInfo}
                        onChange={handleTemplateFormChange}
                        rows={2}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2.5 text-white focus:ring-primary focus:border-primary text-right"
                        placeholder="Enter additional information..."
                        dir="rtl"
                      />
                    </div>
                    
                    {templateFormData.type === 'stage' && (
                      <div className="mt-8 space-y-4">
                        <div className="flex justify-between items-center">
                          <Button 
                            onClick={handleCreateNewSubstep}
                            className="px-2 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                          >
                            Add Substep
                          </Button>
                          <h3 className="text-lg font-semibold text-primary-light text-right">Substeps</h3>
                        </div>

                        <div className="bg-gray-700/40 rounded-lg p-4 border border-gray-600/40">
                          {templateFormData.substeps?.length > 0 ? (
                            <div className="space-y-3 max-h-[300px] overflow-y-auto">
                              {templateFormData.substeps.map((substep, index) => (
                                <div 
                                  key={substep.id || index}
                                  onClick={() => handleSelectSubstep(substep)}
                                  className={`p-3 rounded-lg cursor-pointer ${
                                    selectedSubstep?.id === substep.id
                                      ? 'bg-primary/20 border border-primary/40'
                                      : 'bg-gray-600/50 border border-gray-500/30 hover:bg-gray-600'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700">
                                      {substep.type || 'text'}
                                    </span>
                                    <div className="font-medium text-right">{substep.title}</div>
                                  </div>
                                  <div className="text-xs text-gray-300 mt-1 text-right">
                                    {substep.question}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center text-gray-400 py-4">
                              No substeps. Click "Add Substep" to get started.
                            </div>
                          )}
                        </div>

                        {/* Substep Form */}
                        {(selectedSubstep || substepFormData.id) && (
                          <div className="bg-gray-700/60 rounded-lg p-4 border border-gray-600/60 mt-4">
                            <div className="flex justify-between items-center mb-4">
                              <div className="flex gap-2">
                                <Button 
                                  onClick={handleSaveSubstep}
                                  className="px-2 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs"
                                >
                                  Save
                                </Button>
                                {selectedSubstep && (
                                  <Button 
                                    onClick={handleDeleteSubstep}
                                    className="px-2 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs"
                                  >
                                    Delete
                                  </Button>
                                )}
                              </div>
                              <h4 className="font-medium text-primary-light text-right">
                                {selectedSubstep ? 'Edit Substep' : 'New Substep'}
                              </h4>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2 text-right">
                                <label className="block text-xs text-gray-400">Title</label>
                                <input
                                  type="text"
                                  name="title"
                                  value={substepFormData.title}
                                  onChange={handleSubstepFormChange}
                                  className="w-full bg-gray-600 border border-gray-500 rounded-lg p-2 text-white text-sm focus:ring-primary focus:border-primary text-right"
                                  dir="rtl"
                                />
                              </div>
                              <div className="space-y-2 text-right">
                                <label className="block text-xs text-gray-400">Field Type</label>
                                <select
                                  name="type"
                                  value={substepFormData.type}
                                  onChange={handleSubstepFormChange}
                                  className="w-full bg-gray-600 border border-gray-500 rounded-lg p-2 text-white text-sm focus:ring-primary focus:border-primary text-right"
                                >
                                  <option value="text">Text</option>
                                  <option value="boolean">Yes/No</option>
                                  <option value="select">Single Select</option>
                                  <option value="multiselect">Multi Select</option>
                                  <option value="file">File</option>
                                  <option value="email">Email</option>
                                  <option value="number">Number</option>
                                </select>
                              </div>
                            </div>

                            <div className="space-y-2 mt-3 text-right">
                              <label className="block text-xs text-gray-400">Question</label>
                              <input
                                type="text"
                                name="question"
                                value={substepFormData.question}
                                onChange={handleSubstepFormChange}
                                className="w-full bg-gray-600 border border-gray-500 rounded-lg p-2 text-white text-sm focus:ring-primary focus:border-primary text-right"
                                dir="rtl"
                              />
                            </div>

                            <div className="space-y-2 mt-3 text-right">
                              <label className="block text-xs text-gray-400">Placeholder</label>
                              <input
                                type="text"
                                name="placeholder"
                                value={substepFormData.placeholder}
                                onChange={handleSubstepFormChange}
                                className="w-full bg-gray-600 border border-gray-500 rounded-lg p-2 text-white text-sm focus:ring-primary focus:border-primary text-right"
                                dir="rtl"
                              />
                            </div>

                            <div className="space-y-2 mt-3 text-right">
                              <label className="block text-xs text-gray-400">Help Text</label>
                              <textarea
                                name="helpText"
                                value={substepFormData.helpText}
                                onChange={handleSubstepFormChange}
                                rows={2}
                                className="w-full bg-gray-600 border border-gray-500 rounded-lg p-2 text-white text-sm focus:ring-primary focus:border-primary text-right"
                                dir="rtl"
                              />
                            </div>

                            {/* Options for select / boolean types */}
                            {['select', 'multiselect', 'boolean'].includes(substepFormData.type) && (
                              <div className="space-y-2 mt-3 text-right">
                                <label className="block text-xs text-gray-400">Options</label>
                                
                                <div className="flex gap-2">
                                  <Button
                                    onClick={handleAddOption}
                                    className="px-2 py-1 bg-blue-600 rounded text-white text-xs"
                                  >
                                    Add
                                  </Button>
                                  <input
                                    type="text"
                                    value={optionInput}
                                    onChange={(e) => setOptionInput(e.target.value)}
                                    className="flex-1 bg-gray-600 border border-gray-500 rounded-lg p-2 text-white text-sm focus:ring-primary focus:border-primary text-right"
                                    placeholder="Enter option..."
                                    dir="rtl"
                                  />
                                </div>
                                
                                <div className="mt-2 space-y-1">
                                  {substepFormData.options?.map((option, idx) => (
                                    <div 
                                      key={idx} 
                                      className="flex justify-between items-center bg-gray-700/50 p-2 rounded"
                                    >
                                      <button
                                        onClick={() => handleRemoveOption(option)}
                                        className="text-red-400 hover:text-red-300"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                      </button>
                                      <span className="text-sm text-right">{option}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  /* View mode */
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2 text-right">
                        <label className="block text-sm font-medium text-gray-400">Template Type</label>
                        <p className="text-white bg-gray-700/50 p-2 rounded-lg text-right">{selectedTemplate?.type}</p>
                      </div>
                      <div className="space-y-2 text-right">
                        <label className="block text-sm font-medium text-gray-400">Display Order</label>
                        <p className="text-white bg-gray-700/50 p-2 rounded-lg text-right">{selectedTemplate?.order}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-right">
                      <label className="block text-sm font-medium text-gray-400">Title</label>
                      <p className="text-white bg-gray-700/50 p-2 rounded-lg text-right">{selectedTemplate?.title}</p>
                    </div>
                    
                    <div className="space-y-2 text-right">
                      <label className="block text-sm font-medium text-gray-400">Description</label>
                      <p className="text-white bg-gray-700/50 p-3 rounded-lg text-right">{selectedTemplate?.description}</p>
                    </div>
                    
                    {selectedTemplate?.additionalInfo && (
                      <div className="space-y-2 text-right">
                        <label className="block text-sm font-medium text-gray-400">Additional Information</label>
                        <p className="text-white bg-gray-700/50 p-3 rounded-lg text-right">{selectedTemplate.additionalInfo}</p>
                      </div>
                    )}
                    
                    {selectedTemplate?.type === 'stage' && selectedTemplate?.substeps?.length > 0 && (
                      <div className="mt-8 space-y-4">
                        <h3 className="text-lg font-semibold text-primary-light text-right">Substeps ({selectedTemplate.substeps.length})</h3>
                        
                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                          {selectedTemplate.substeps.map((substep, index) => (
                            <div 
                              key={substep.id || index}
                              className="p-4 rounded-lg bg-gray-700/40 border border-gray-600/30"
                            >
                              <div className="flex justify-between items-start mb-3">
                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-600">
                                  {substep.type || 'text'}
                                </span>
                                <h4 className="font-medium text-primary-light text-right">{substep.title}</h4>
                              </div>
                              
                              <div className="text-sm text-gray-300 mb-3 text-right">{substep.question}</div>
                              
                              {substep.helpText && (
                                <div className="text-xs text-gray-400 mt-2 mb-3 bg-gray-800/60 p-2 rounded text-right">
                                  <strong>Help Text:</strong> {substep.helpText}
                                </div>
                              )}
                              
                              {(substep.type === 'boolean' || substep.type === 'select' || substep.type === 'multiselect') && substep.options?.length > 0 && (
                                <div className="mt-3">
                                  <div className="text-xs font-medium text-gray-400 mb-1 text-right">Options:</div>
                                  <div className="flex flex-wrap gap-2">
                                    {substep.options.map((option, idx) => (
                                      <span key={idx} className="text-xs bg-gray-600/50 py-1 px-2 rounded">
                                        {option}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 h-full flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  </svg>
                  <p className="text-lg">Select an existing template from the list or create a new one</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CardTemplatesSection; 